import { TextShimmer } from "@/components/text-shimmer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { buildHotelRoutePreview } from "@/feature/hotel/checkout/lib/hotel-route-preview";
import {
  setHotelPage,
  setSelectHotel,
} from "@/feature/hotel/checkout/state/hotel-data.slice";
import { encodedHotelParams } from "@/feature/hotel/search/lib/hotel-formatter-helper";
import {
  appendCustomUmrahSearchContext,
} from "@/feature/umrah/custom/request/custom-umrah-flow";
import { useSearchServiceFlow } from "@/feature/umrah/custom/request/hooks/use-search-service-flow";
import { priceFormatter } from "@/lib/price-formatter";
import { useAppDispatch } from "@/redux/hooks";
import { ChevronsRight, HandPlatter, MapPinCheck } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import { IHotelList } from "../../types/hotel-search-page-type";
import { IHotelsSearchSchema } from "../../types/hotel-search-type";
import HotelListImageCarousel from "../shared/hotel-list-image-carousel";
import { renderHotelRatingStars } from "../shared/hotel-rating-stars";

type Props = {
  hotel: IHotelList;
  searchData: IHotelsSearchSchema;
  no_of_night: number | undefined;
  parentSearchId?: string;
};

const HotelListCard = ({
  hotel,
  searchData,
  no_of_night,
  parentSearchId,
}: Props) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { source, umrahCity } = useSearchServiceFlow();

  const [isPending, startTransition] = useTransition();

  const mealPlans =
    hotel.details?.boarding_details
      ?.map((item) => item.trim())
      .filter(Boolean) || [];

  const visibleMealPlans = mealPlans.slice(0, 3);
  const hiddenMealPlanCount = Math.max(
    mealPlans.length - visibleMealPlans.length,
    0,
  );
  const totalPrice = Number(hotel.price_details?.total_price ?? 0);
  const hasPrice = Number.isFinite(totalPrice) && totalPrice > 0;
  const formattedTotalPrice = hasPrice ? priceFormatter(totalPrice) : "N/A";
  const nightlyPrice =
    hasPrice && no_of_night
      ? priceFormatter(Math.round(totalPrice / no_of_night))
      : null;
  const fullAddress = [
    hotel.contact_details?.address,
    hotel.contact_details?.country,
  ]
    .filter(Boolean)
    .join(", ");
  const normalizedParentSearchId = parentSearchId ?? hotel.parent_search_id;
  const previewHotel = buildHotelRoutePreview(hotel);
  const routeQuery = new URLSearchParams();
  const encodedSearchParams = new URLSearchParams(encodedHotelParams(searchData));

  encodedSearchParams.forEach((value, key) => {
    routeQuery.set(key, value);
  });
  appendCustomUmrahSearchContext(routeQuery, {
    cityKey: umrahCity,
    source,
  });

  routeQuery.set("searchId", hotel.search_id || hotel.hotel_code);
  if (normalizedParentSearchId) {
    routeQuery.set("parent_search_id", normalizedParentSearchId);
  }
  routeQuery.set("hotel_name", previewHotel.name);
  routeQuery.set("hotel_address", previewHotel.address);
  if (previewHotel.latitude !== null) {
    routeQuery.set("hotel_lat", String(previewHotel.latitude));
  }
  if (previewHotel.longitude !== null) {
    routeQuery.set("hotel_lng", String(previewHotel.longitude));
  }

  const routePath = `/hotels/${encodeURIComponent(hotel.hotel_code)}?${routeQuery.toString()}`;

  const handleAvailability = () => {
    const nextHotel = normalizedParentSearchId
      ? {
          ...hotel,
          parent_search_id: normalizedParentSearchId,
        }
      : hotel;

    dispatch(setSelectHotel(nextHotel));
    dispatch(setHotelPage("hotel-details"));
    startTransition(() => {
      router.push(routePath);
    });
  };

  return (
    <Card className="group w-full overflow-hidden rounded-2xl border border-border/70 bg-background p-0 shadow-sm transition-all duration-300  ">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_15.5rem]">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
          <div className="w-full overflow-hidden bg-muted/40">
            <HotelListImageCarousel images={hotel.images} />
          </div>

          <div className="flex min-w-0 flex-col justify-between gap-3 px-4 py-3.5 md:px-5">
            <div className="space-y-2.5">
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="line-clamp-1 text-lg font-semibold leading-snug text-foreground">
                      {hotel.name}
                    </h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <div className="flex">
                        {renderHotelRatingStars(hotel.category, "size-[15px]")}
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {hotel.category} Star Hotel
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPinCheck className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span className="line-clamp-2 leading-5">
                    {fullAddress || "Address unavailable"}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-xs">
                <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <HandPlatter className="size-3.5 text-primary" />
                  <span>Meal Plan</span>
                </div>
                {visibleMealPlans.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {visibleMealPlans.map((item, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary"
                      >
                        {item}
                      </Badge>
                    ))}
                    {hiddenMealPlanCount > 0 ? (
                      <Badge
                        variant="secondary"
                        className="rounded-full px-2 py-0.5 text-[10px]"
                      >
                        +{hiddenMealPlanCount}
                      </Badge>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Unavailable</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2 ">
                <Badge
                  className={`rounded-full border bg-background/80 px-2.5 py-1 font-medium backdrop-blur-sm ${
                    hotel.refundable
                      ? "border-green-600 text-green-600"
                      : "border-red-600 text-red-600"
                  }`}
                >
                  {hotel.refundable ? "Refundable" : "Non-Refundable"}
                </Badge>

                {hotel.refundable ? (
                  <Badge
                    variant="secondary"
                    className={`rounded-full px-2.5 py-1 text-[11px] ${
                      hotel.cancellation_policy?.free_cancellation
                        ? "bg-green-500/10 text-green-600"
                        : "bg-amber-500/10 text-amber-600"
                    }`}
                  >
                    {hotel.cancellation_policy?.free_cancellation
                      ? "Free Cancellation"
                      : "Partial Cancellation"}
                  </Badge>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border/70 bg-gradient-to-br from-primary/[0.08] via-background to-background xl:border-l xl:border-t-0">
          <div className="flex h-full w-full flex-col justify-between gap-4 px-4 py-4 xl:items-end xl:text-right">
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Total for {no_of_night ?? 0} nights
                </p>
                <p className="text-2xl font-bold leading-none text-foreground">
                  {formattedTotalPrice}
                </p>
                {nightlyPrice ? (
                  <p className="text-xs text-muted-foreground">
                    Approx. {nightlyPrice} / night
                  </p>
                ) : null}
              </div>
            </div>

            <Button
              onClick={handleAvailability}
              className="h-11 w-full rounded-xl px-4 font-semibold xl:w-full"
            >
              {isPending ? (
                <TextShimmer>See availability</TextShimmer>
              ) : (
                "See availability"
              )}{" "}
              <ChevronsRight />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default HotelListCard;



